import React from 'react';
import {
  type RouteProp,
  useIsFocused,
  useRoute,
} from '@react-navigation/native';

import { MenuContent, useDeliveryMenuContentData } from '../../components';
import type { MenuTabStackParamList } from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const DeliveryMenuScreen = () => {
  //
  // ─── Navigation ──────────────────────────────────────────────────────

  const { params } =
    useRoute<RouteProp<MenuTabStackParamList, 'DeliveryMenu'>>();

  const { addressId } = params ?? {};

  // ─── Remote Data ─────────────────────────────────────────────────────

  const {
    restaurantData,
    deliveryDetailsData,
    isFetching,
    hasError,
    hasNoProducts,
  } = useDeliveryMenuContentData({ addressId });

  // ─────────────────────────────────────────────────────────────────────

  const isScreenFocused = useIsFocused();
  const shouldRenderErrorView =
    hasError || !restaurantData || !deliveryDetailsData || hasNoProducts;

  // When the menu screen is rendered without being focused, there won't be any data available.
  const isWaitingForFocus = !isScreenFocused && shouldRenderErrorView;

  if (isFetching || isWaitingForFocus) {
    return <MenuContent.Loading />;
  }

  if (shouldRenderErrorView) {
    return <MenuContent.Error />;
  }

  // ─────────────────────────────────────────────────────────────────────

  const locationName =
    deliveryDetailsData.addressName ?? deliveryDetailsData?.shortAddress;

  return (
    <MenuContent.Ready
      // NOTE: We use the address ID as the key to force a re-render when the
      //       delivery details data changes to prevent having a stale data and components.
      key={deliveryDetailsData.addressId}
      restaurantData={restaurantData}
      deliveryDetailsData={deliveryDetailsData}
      orderChannel="delivery"
      locationName={locationName}
    />
  );
};
